// Added By Farhan Khan Khan on 24-Dec-2025
// Extended By System on 29-Jan-2026 - Added Mounting Structure BOM support
// Updated By System on 11-Apr-2026 - Cleaned up legacy mounting structure logic
/* eslint-disable no-lone-blocks */
import React, { useState, useEffect } from "react";
import { Col, Container, Row, Table, Button as BootstrapButton } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import solisPowerApi from "../../api/solisPowerApi";
import Helmet from "react-helmet";
import { getUserDetails } from "../../utils/helper";
import PubSub from "pubsub-js";
import Select from "react-select";
import Main from "../layout/Main";
import { handleUppercaseChange } from "../common/UpperCaseMethod";
import { BOM_CATEGORY_OPTIONS, BOM_MAKE_OPTIONS } from "../../constants/bomConstants";

const BomEdit = (props) => {
  const { id } = useParams();
  const userInfo = getUserDetails();
  const [validated, setValidated] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [bomType, setBomType] = useState('STANDARD');
  const [bom, setBom] = useState({ name: "", status: "Active", bom_type: "STANDARD" });
  const [items, setItems] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [itemErrors, setItemErrors] = useState({});

  const unitOptions = [
    { value: "Nos", label: "Nos" },
    { value: "Set", label: "Set" },
    { value: "Mtr", label: "Mtr" },
    { value: "SqM", label: "SqM" },
    { value: "Kg", label: "Kg" },
    { value: "KWp", label: "KWp" },
    { value: "KWh", label: "KWh" },
    { value: "Box", label: "Box" },
    { value: "Lot", label: "Lot" }
  ];

  useEffect(() => {
    async function init() {
      setItems([]);
      await fetchMaterialProducts();
      await fetchActiveBrands();

      if (id) {
        await loadBomCategoryWithItems();
      } else {
        setBom(prev => ({ ...prev, name: prev.name || "BOM" }));
      }
    }
    init();
  }, [id]);

  const loadBomCategoryWithItems = async () => {
    try {
      const bomData = await solisPowerApi.fetchBomCategoryWithItems(id);
      if (bomData) {
        setBomType('STANDARD');
        setBom({
          id: bomData.id,
          name: bomData.name,
          status: bomData.status,
          bom_type: 'STANDARD'
        });

        const toArr = (v) => !v ? [] : Array.isArray(v) ? v : [v];
        const itemsWithParsedParts = (bomData.items || []).map(item => {
          if (item.parts) {
            if (typeof item.parts === 'string') {
              try {
                item.parts = JSON.parse(item.parts);
              } catch (e) {
                console.warn('Failed to parse parts JSONB:', e);
                item.parts = [];
              }
            }
            if (!Array.isArray(item.parts)) {
              item.parts = [];
            }
          } else {
            item.parts = [];
          }
          item.brand_id = toArr(item.brand_id);
          return item;
        });

        setItems(itemsWithParsedParts);
      }
    } catch (error) {
      console.error("Error fetching BOM:", error);
    }
  };

  const fetchMaterialProducts = async () => {
    const products = await solisPowerApi.fetchMaterialProducts();
    if (products) {
      const options = products.map(product => ({
        value: product.id,
        label: product.name,
        product: product
      }));
      setProductOptions(options);
    }
  };

  const fetchActiveBrands = async () => {
    const brands = await solisPowerApi.fetchActiveBrands();
    if (brands) {
      const options = brands.map(brand => ({
        value: brand.id,
        label: brand.name
      }));
      setBrandOptions(options);
    }
  };

  const handleBomChange = (e) => {
    const { name, value } = e.target;
    setBom({ ...bom, [name]: value });
  };

  const toBrandIdsArr = (v) => !v ? [] : Array.isArray(v) ? v : [v];
  const toBrandIdPayload = (v) => { const a = toBrandIdsArr(v); return a.length > 0 ? a : null; };

  const addItemRow = () => {
    const newItem = {
      id: `temp_${Date.now()}`,
      s_no: items.length + 1,
      product_id: null,
      product_name: null,
      specification: "",
      category: "",
      size_range_capacity: "",
      make: "",
      unit: "Nos",
      quantity: 1,
      manufacturer_warranty: "",
      name: "",
      description: "",
      brand_id: [],
      status: "Active"
    };
    setItems([...items, newItem]);
  };

  const removeItemRow = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    newItems.forEach((item, i) => { item.s_no = i + 1; });
    setItems(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);

    const errorKey = `${index}_${field}`;
    const newErrors = { ...itemErrors };
    delete newErrors[errorKey];
    setItemErrors(newErrors);
  };

  const handleProductChange = async (index, selectedOption) => {
    const newItems = [...items];
    const item = newItems[index];

    if (selectedOption) {
      item.product_id = selectedOption.value;
      item.product_name = selectedOption.label;
      item.name = selectedOption.label;
      item.description = selectedOption.label;
      try {
        const productDetails = await solisPowerApi.fetchProductDetailsForBom(selectedOption.value);
        if (productDetails) {
          item.description = productDetails.description || item.description || item.product_name;
          item.brand_id = toBrandIdsArr(productDetails.brand_id || item.brand_id);
        }
      } catch (error) {
        console.error("Error fetching product details:", error);
      }
    } else {
      item.product_id = null;
      item.product_name = null;
      item.name = "";
      item.description = "";
      item.brand_id = [];
    }

    setItems(newItems);
  };

  const handleBrandChange = (index, selected) => {
    const arr = selected && Array.isArray(selected) ? selected : [];
    handleItemChange(index, "brand_id", arr.map(s => s.value));
  };

  const handleUnitChange = (index, selectedOption) => {
    handleItemChange(index, "unit", selectedOption ? selectedOption.value : null);
  };

  const validateBom = () => {
    if (!bom.name || !bom.name.trim()) return "BOM name is required";
    if (!bom.status) return "Status is required";
    return null;
  };

  const validateItems = () => {
    const errors = {};
    const toastMessages = [];

    items.forEach((item, index) => {
      if (!item.product_id) {
        errors[`${index}_product_id`] = "Product is required";
        toastMessages.push(`Please select Product for Item #${index + 1}`);
      }
      if (!item.unit || !item.unit.trim()) {
        errors[`${index}_unit`] = "Unit is required";
        toastMessages.push(`Please fill Unit for Item #${index + 1}`);
      }
      if (!toBrandIdsArr(item.brand_id).length) {
        errors[`${index}_brand_id`] = "Brand is required";
        toastMessages.push(`Please select Brand for Item #${index + 1}`);
      }
    });

    setItemErrors(errors);

    if (Object.keys(errors).length > 0) {
      return { isValid: false, message: toastMessages[0] || "Please fill all required fields" };
    }
    return { isValid: true, message: null };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    const bomError = validateBom();
    if (bomError) {
      PubSub.publish("RECORD_ERROR_TOAST", { title: "Validation Error", message: bomError });
      return;
    }

    let bomToSave = {
      name: bom.name,
      status: bom.status,
      bom_type: 'STANDARD'
    };

    if (bom.id) {
      bomToSave.id = bom.id;
    }

    let bomResult;
    if (bom.id) {
      bomResult = await solisPowerApi.saveBomCategory(bomToSave);
    } else {
      bomResult = await solisPowerApi.createBomCategory(bomToSave);
    }

    if (!bomResult || !bomResult.success) {
      PubSub.publish("RECORD_ERROR_TOAST", { title: "Error", message: "Failed to save BOM header" });
      return;
    }

    const savedBomId = bomResult.id || bom.id;

    if (items.length > 0) {
      const validationResult = validateItems();
      if (!validationResult.isValid) {
        PubSub.publish("RECORD_ERROR_TOAST", { title: "Validation Error", message: validationResult.message });
        return;
      }

      const standardItemsToSave = items.map((item, index) => ({
        s_no: item.s_no ?? (index + 1),
        name: item.name || item.product_name || "BOM Item",
        specification: item.specification || null,
        category: item.category || null,
        size_range_capacity: item.size_range_capacity || null,
        make: item.make || null,
        unit: item.unit || "Nos",
        quantity: item.quantity ?? 1,
        manufacturer_warranty: item.manufacturer_warranty || null,
        description: item.description || item.product_name || "BOM Item",
        status: item.status || "Active",
        bom_categories_id: savedBomId,
        item_type: 'STANDARD',
        product_id: item.product_id || null,
        brand_id: toBrandIdPayload(item.brand_id)
      }));

      const standardItemsResult = await solisPowerApi.bulkCreateBomMasterItems(standardItemsToSave);

      if (!standardItemsResult || standardItemsResult.errors) {
        PubSub.publish("RECORD_ERROR_TOAST", { title: "Error", message: standardItemsResult?.errors || "Failed to save items" });
        return;
      }
    }

    PubSub.publish("RECORD_SAVED_TOAST", { title: "Success", message: "BOM Saved Successfully" });
    navigate("/boms");
  };

  return (
    <Main>
      <Helmet title={id ? "Edit BOM" : "Create BOM"} />
      <Container fluid className="mt-4">
        <Row className="mb-4">
          <Col md={8}>
            <h3 className="mb-0">{id ? "Edit BOM" : "Create BOM"}</h3>
          </Col>
          <Col md={4} className="text-end">
            <Link to="/boms">
              <Button variant="outline-secondary" className="me-2">Cancel</Button>
            </Link>
            <Button variant="primary" onClick={handleSubmit}>
              {id ? "Update BOM" : "Create BOM"}
            </Button>
          </Col>
        </Row>

        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>BOM Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={bom.name}
                      onChange={handleBomChange}
                      isInvalid={!!(validated && !bom.name)}
                      required
                    />
                    <Form.Control.Feedback type="invalid">Please provide a BOM name.</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Select
                      options={[{ value: "Active", label: "Active" }, { value: "Inactive", label: "Inactive" }]}
                      value={{ value: bom.status, label: bom.status }}
                      onChange={(opt) => setBom({ ...bom, status: opt.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-header bg-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Bill of Material Items</h5>
              <Button variant="primary" size="sm" onClick={addItemRow}>Add Item</Button>
            </div>
            <div className="card-body p-0">
              <Table responsive className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th style={{ width: '50px' }}>S.No</th>
                    <th style={{ width: '15%' }}>Product</th>
                    <th style={{ width: '20%' }}>Specification</th>
                    <th style={{ width: '10%' }}>Category</th>
                    <th style={{ width: '10%' }}>Unit</th>
                    <th style={{ width: '8%' }}>Qty</th>
                    <th style={{ width: '15%' }}>Brand</th>
                    <th style={{ width: '50px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={item.id || index}>
                      <td>{item.s_no}</td>
                      <td>
                        <Select
                          options={productOptions}
                          value={productOptions.find(opt => opt.value === item.product_id)}
                          onChange={(opt) => handleProductChange(index, opt)}
                        />
                      </td>
                      <td>
                        <Form.Control type="text" value={item.specification} onChange={(e) => handleItemChange(index, "specification", e.target.value)} />
                      </td>
                      <td>
                        <Form.Control type="text" value={item.category} onChange={(e) => handleItemChange(index, "category", e.target.value)} />
                      </td>
                      <td>
                        <Select options={unitOptions} value={unitOptions.find(opt => opt.value === item.unit)} onChange={(opt) => handleUnitChange(index, opt)} />
                      </td>
                      <td>
                        <Form.Control type="number" value={item.quantity} onChange={(e) => handleItemChange(index, "quantity", e.target.value)} />
                      </td>
                      <td>
                        <Select isMulti options={brandOptions} value={brandOptions.filter(opt => toBrandIdsArr(item.brand_id).includes(opt.value))} onChange={(selected) => handleBrandChange(index, selected)} />
                      </td>
                      <td>
                        <Button variant="link" className="text-danger p-0" onClick={() => removeItemRow(index)}>
                          <i className="bi bi-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan="8" className="text-center py-4 text-muted">No items added yet. Click "Add Item" to begin.</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </Form>
      </Container>
    </Main>
  );
};

export default BomEdit;
